// Intercom utility to enable showing and hiding the widget from any component

// App ID should ideally be pulled from environment variables
const INTERCOM_APP_ID = "l2eh5w43"; // TODO: Change to environment variable

/**
 * Types for Intercom methods
 */
interface IntercomCommand {
  (command: "boot", settings: any): void;
  (command: "update", settings?: any): void;
  (command: "show"): void;
  (command: "hide"): void;
  (command: "shutdown"): void;
  (command: "trackEvent", eventName: string, metadata?: any): void;
  (command: string, ...args: any[]): void;
}

/**
 * Declaration for the Intercom global
 */
declare global {
  interface Window {
    Intercom?: IntercomCommand;
    intercomSettings?: any;
  }
}

/**
 * Initialize Intercom with the user's data
 * This should be called once during app bootstrap if you want Intercom available throughout the app
 */
export const initializeIntercom = (userData?: {
  email?: string;
  username?: string;
  askrose?: boolean;
}): void => {
  // Set intercom settings
  window.intercomSettings = {
    app_id: INTERCOM_APP_ID,
    ...userData,
  };

  // If Intercom already exists, just update it
  if (window.Intercom) {
    window.Intercom("update", window.intercomSettings);
    return;
  }

  // Load the Intercom script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
  document.body.appendChild(script);

  // Initialize Intercom
  const w = window as any;
  const ic = w.Intercom;
  if (typeof ic === "function") {
    ic("reattach_activator");
    ic("update", w.intercomSettings);
  } else {
    const i = function () {
      i.c(arguments);
    } as any;
    i.q = [];
    i.c = function (args: IArguments | any[]) {
      i.q.push(args);
    };
    w.Intercom = i;
  }
};

/**
 * Show the Intercom widget
 */
export const showIntercom = (): void => {
  if (!window.Intercom) {
    // Initialize Intercom if it's not loaded yet
    initializeIntercom();

    // Short delay to allow Intercom to initialize before showing
    setTimeout(() => {
      window.Intercom?.("show");
    }, 500);
    return;
  }
};

/**
 * Hide the Intercom widget
 */
export const hideIntercom = (): void => {
  if (window.Intercom) {
    // Hide the widget
    window.Intercom("hide");

    // Also hide the launcher by updating the settings to hide it
    window.Intercom("update", {
      hide_default_launcher: true,
    });
  }
};

/**
 * Update Intercom with new user data
 */
export const updateIntercom = (userData: any): void => {
  if (window.Intercom) {
    window.Intercom("update", userData);
  }
};

/**
 * Track an event in Intercom
 */
export const trackIntercomEvent = (eventName: string, metadata?: any): void => {
  if (window.Intercom) {
    window.Intercom("trackEvent", eventName, metadata);
  }
};

/**
 * Shutdown Intercom completely
 */
export const shutdownIntercom = (): void => {
  if (window.Intercom) {
    window.Intercom("shutdown");
  }
};

export default {
  initialize: initializeIntercom,
  show: showIntercom,
  hide: hideIntercom,
  update: updateIntercom,
  trackEvent: trackIntercomEvent,
  shutdown: shutdownIntercom,
};
